import React, { memo, useEffect, useRef } from 'react';
import { FaCompressAlt, FaVideo } from 'react-icons/fa';
import { Button } from '../common/Button';
import { useGameInstance } from '../../contexts/GameInstanceContext';
import { phaserKeyboardHolds, suspendPhaserKeyboard } from '../../utils/phaser-keyboard.js';
import { VIDEO_CONFIG } from '../../config/videoConfig.js';

interface FullScreenVideoProps {
  /** The call, for the top bar: the private area's name. */
  label?: string;

  /** People in the call, for the top bar. Omitted while there is nothing to count (a reconnect without a room). */
  participantCount?: number;

  /**
   * Width the chat drawer reports through `onWidthChange` (0 when closed). On
   * md and up the drawer is 600px wide beside the video, so the overlay starts
   * to its right and the tiles re-fit to what is left; below md the drawer is
   * full width with a backdrop and simply covers the overlay.
   */
  chatDrawerWidth?: number;

  /**
   * Between the call label and the exit controls: the "<name> is sharing ·
   * Pin" chip (#124). It may shrink, so a narrow top bar truncates it rather
   * than pushing the exit off screen.
   */
  topBarSlot?: React.ReactNode;

  /** A status panel (reconnecting, error) laid over the top of the video area. */
  status?: React.ReactNode;

  /** Leave full screen: the Exit button, and Esc outside a text field. */
  onExit: () => void;

  /** The layout: tiled, or pinned (#124). */
  children?: React.ReactNode;
}

/** Esc here is for typing, not for leaving full screen. */
function isTextEntry(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT') {
    return true;
  }
  return target.isContentEditable || target.closest('[contenteditable=""], [contenteditable="true"]') !== null;
}

/**
 * The full-screen video overlay (issue #123, epic #122 §3.2 and §4).
 *
 * It covers the game from the top of the viewport to the top of the bottom
 * bar, above the world and *below* the drawers (`z-[2000]`) and the bottom bar
 * (`z-[2001]`), so the chat, the menu and the media controls stay usable. The
 * layout inside it is the caller's; this is the chrome around it: a labelled
 * region, the top bar and the exits.
 *
 * While it is mounted:
 * - the Phaser keyboard is held (`video-fullscreen`), so arrow keys and WASD
 *   cannot walk the avatar out of the private area, which would end the call.
 *   Closing the chat or a modal can no longer switch movement back on.
 * - the game scene is not rendered. Phaser keeps *updating* an invisible
 *   scene, so network updates keep the world current for when the overlay
 *   goes away; only the drawing of a canvas nobody can see is skipped.
 * - Esc leaves full screen, except while typing, while a modal is open, and
 *   when another layer (the chat drawer, a menu) has already handled that Esc.
 *
 * All three are tied to the mount, not to the view state, so however the
 * overlay goes away (exit, the call ending, a crash caught by the video error
 * boundary) the keyboard and the world come back with it.
 */
const FullScreenVideoComponent: React.FC<FullScreenVideoProps> = ({
  label,
  participantCount,
  chatDrawerWidth = 0,
  topBarSlot,
  status,
  onExit,
  children
}) => {
  const regionRef = useRef<HTMLElement>(null);
  const { gameInstance, getGameScene } = useGameInstance();
  const scene = getGameScene();

  // Focus moves into the overlay on enter, so Esc and Tab start here. Not
  // out of a text field, though: the overlay is also rebuilt when a dropped
  // room comes back during a reconnect, and that must not pull focus out of
  // the chat composer while the viewer is typing.
  useEffect(() => {
    if (isTextEntry(document.activeElement)) return;
    regionRef.current?.focus({ preventScroll: true });
  }, []);

  // Hold the keyboard for as long as the overlay is up.
  useEffect(() => suspendPhaserKeyboard(gameInstance, 'video-fullscreen'), [gameInstance]);

  // Stop drawing the covered world; keep updating it.
  useEffect(() => {
    const systems = scene?.sys;
    if (!systems?.setVisible) return;
    const wasVisible = systems.isVisible?.() ?? true;
    systems.setVisible(false);
    return () => {
      systems.setVisible?.(wasVisible);
    };
  }, [scene]);

  // Esc leaves full screen. Registered on `window`, after the document-level
  // handlers of the chat drawer and the dropdown menus, which mark the Esc
  // they consume.
  const onExitRef = useRef(onExit);
  useEffect(() => {
    onExitRef.current = onExit;
  }, [onExit]);
  const gameRef = useRef(gameInstance);
  useEffect(() => {
    gameRef.current = gameInstance;
  }, [gameInstance]);

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      if (isTextEntry(event.target)) return;
      if (phaserKeyboardHolds(gameRef.current).includes('modal')) return;
      onExitRef.current();
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const insetStyle = {
    '--full-screen-video-left': `${Math.max(0, chatDrawerWidth)}px`
  } as React.CSSProperties;

  return (
    <section
      ref={regionRef}
      tabIndex={-1}
      aria-label="Video call, full screen"
      data-testid="full-screen-video"
      data-lk-theme={VIDEO_CONFIG.theme}
      style={insetStyle}
      className={`@container fixed inset-x-0 top-0 bottom-16 md:left-[var(--full-screen-video-left)] ${VIDEO_CONFIG.styles.zIndex} pointer-events-auto flex flex-col bg-game-navy text-gray-200 outline-none`}
    >
      <header className="flex h-13 shrink-0 items-center gap-4 border-b border-[var(--lk-bg3)] bg-[var(--lk-bg2)] px-3 md:h-14 md:px-6">
        <div className="flex min-w-0 flex-1 items-center gap-2.5">
          <FaVideo aria-hidden="true" className="shrink-0 text-game-light" />
          <h2 className="truncate text-sm font-semibold text-game-cream md:text-[15px]">{label?.trim() || 'Video call'}</h2>
          {participantCount !== undefined && (
            <span className="shrink-0 whitespace-nowrap text-sm text-game-light">
              {participantCount} in call
            </span>
          )}
        </div>

        {topBarSlot && <div className="flex min-w-0 items-center">{topBarSlot}</div>}

        <div className="flex flex-1 items-center justify-end gap-2.5">
          <kbd
            title="Press Esc to exit full screen"
            className="hidden rounded border border-[var(--lk-bg4)] px-1.5 py-0.5 font-sans text-xs text-game-light @min-[1000px]:inline-block"
          >
            Esc
          </kbd>
          <Button
            type="button"
            variant="secondary"
            onClick={onExit}
            aria-label="Exit full screen"
            title="Exit full screen"
            className="h-10 px-3 text-sm text-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-game-golden focus-visible:ring-offset-2 focus-visible:ring-offset-game-navy"
          >
            <FaCompressAlt aria-hidden="true" className="text-sm" />
            <span className="hidden @min-[480px]:inline">Exit full screen</span>
          </Button>
        </div>
      </header>

      <div className="relative min-h-0 flex-1">
        {children}
        {status && (
          <div className="pointer-events-none absolute inset-x-0 top-4 flex justify-center px-4 *:pointer-events-auto">
            {status}
          </div>
        )}
      </div>
    </section>
  );
};

export const FullScreenVideo = memo(FullScreenVideoComponent);
FullScreenVideo.displayName = 'FullScreenVideo';
